import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Bell, CheckCircle2, AlertCircle, Send, MessageSquare,
  Check, Building2, FileCheck2, TrendingDown,
  Gauge, UserCheck, Clock, CalendarClock, PenLine, FileUp, RefreshCcw, Sparkles, Lock, Unlock, GitMerge, Activity, BadgeDollarSign, SlidersHorizontal, LayoutTemplate,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_CONFIG = {
  pipeline_merged: { icon: GitMerge, color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Pipeline' },
  factor_shift: { icon: Activity, color: 'text-rose-700', bg: 'bg-rose-50', label: 'Factor shift' },
  rate_change: { icon: BadgeDollarSign, color: 'text-amber-700', bg: 'bg-amber-50', label: 'Rate change' },
  reprice_approval: { icon: RefreshCcw, color: 'text-[#0F2040]', bg: 'bg-cyan-50', label: 'Approval' },
  subscription_approval: { icon: BadgeDollarSign, color: 'text-rose-700', bg: 'bg-rose-50', label: 'Guardrail approval' },
  subscription_approval_result: { icon: BadgeDollarSign, color: 'text-[#0F2040]', bg: 'bg-cyan-50', label: 'Approval result' },
  quote_expiry: { icon: CalendarClock, color: 'text-amber-700', bg: 'bg-amber-50', label: 'Quote expiring' },
  break_even_alert: { icon: TrendingDown, color: 'text-rose-700', bg: 'bg-rose-50', label: 'Break-even alert' },
  break_even_reminder: { icon: TrendingDown, color: 'text-amber-700', bg: 'bg-amber-50', label: 'Break-even reminder' },
  cac_calibration_approval: { icon: SlidersHorizontal, color: 'text-amber-700', bg: 'bg-amber-50', label: 'CAC calibration' },
  cac_calibration_result: { icon: SlidersHorizontal, color: 'text-[#0F2040]', bg: 'bg-cyan-50', label: 'CAC calibration' },
  estimate_locked: { icon: Lock, color: 'text-amber-700', bg: 'bg-amber-50', label: 'Locked' },
  estimate_unlocked: { icon: Unlock, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Unlocked' },
  approval: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Approved' },
  changes_requested: { icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50', label: 'Changes' },
  quote_sent: { icon: Send, color: 'text-teal-600', bg: 'bg-teal-50', label: 'Sent' },
  comment: { icon: MessageSquare, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Comment' },
  transcript_ready: { icon: FileCheck2, color: 'text-cyan-600', bg: 'bg-cyan-50', label: 'Transcript' },
  wave_slip_alert: { icon: TrendingDown, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Slip alert' },
  rfp_low_client_score: { icon: Gauge, color: 'text-rose-600', bg: 'bg-rose-50', label: 'Low client score' },
  rfp_question_assigned: { icon: UserCheck, color: 'text-sky-600', bg: 'bg-sky-50', label: 'Assigned' },
  rfp_question_reminder: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Reminder' },
  rfp_owner_assigned: { icon: UserCheck, color: 'text-sky-600', bg: 'bg-sky-50', label: 'RFP owner' },
  rfp_stage_stalled: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Stalled' },
  rfp_deadline: { icon: CalendarClock, color: 'text-rose-600', bg: 'bg-rose-50', label: 'Deadline' },
  rfp_signoff: { icon: PenLine, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Sign-off' },
  rfp_import_stage: { icon: FileUp, color: 'text-cyan-600', bg: 'bg-cyan-50', label: 'Import' },
  rfp_rematch_nudge: { icon: RefreshCcw, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Rematch' },
  rfp_parse_done: { icon: FileCheck2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Parse done' },
  rfp_template_suggestion: { icon: LayoutTemplate, color: 'text-[#0F2040]', bg: 'bg-[#4ABFCF]/10', label: 'Saved layout available' },
  rfp_cleanup_ready: { icon: Sparkles, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Library review' },
  rfp_rescore_done: { icon: Gauge, color: 'text-teal-600', bg: 'bg-teal-50', label: 'Rescore done' },
  rfp_health_weekly: { icon: Gauge, color: 'text-sky-600', bg: 'bg-sky-50', label: 'Health recap' },
  rfp_submission_due: { icon: Send, color: 'text-rose-600', bg: 'bg-rose-50', label: 'Submission due' },
  rfp_reviewer_digest: { icon: PenLine, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Awaiting sign-off' },
  rfp_reviewer_assigned: { icon: UserCheck, color: 'text-sky-600', bg: 'bg-sky-50', label: 'Reviewer' },
};

function timeAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [entityDrift, setEntityDrift] = useState({ count: 0, entity_id: null });
  const prevUnreadRef = useRef(0);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications?limit=15`, { withCredentials: true });
      const data = res.data;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);

      // Toast for new notifications
      if (data.unread_count > prevUnreadRef.current && prevUnreadRef.current > 0) {
        const newest = (data.notifications || [])[0];
        if (newest && !newest.read) {
          const cfg = NOTIF_CONFIG[newest.type] || NOTIF_CONFIG.comment;
          toast(newest.message, {
            description: newest.estimate_name,
            icon: <cfg.icon className={`w-4 h-4 ${cfg.color}`} />,
            action: {
              label: 'View',
              onClick: () => navigate(newest.link || `/workspace/${newest.estimate_id}`),
            },
          });
        }
      }
      prevUnreadRef.current = data.unread_count || 0;
    } catch (err) {
      // silently fail polling
    }
  }, [navigate]);

  const fetchEntityDrift = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/auth/preferences/entity-drift-count`,
        { withCredentials: true },
      );
      setEntityDrift({
        count: Number(res.data?.count || 0),
        entity_id: res.data?.entity_id || null,
      });
    } catch {
      setEntityDrift({ count: 0, entity_id: null });
    }
  }, []);

  // Initial fetch + polling every 30s
  useEffect(() => {
    fetchNotifications();
    fetchEntityDrift();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchEntityDrift();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchEntityDrift]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { /* ignore */ }
  };

  const snoozeFactorShift = async (e) => {
    e.stopPropagation();
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/factor-shift/snooze`, { days: 30 }, { withCredentials: true });
      toast.success(`Factor shift alerts snoozed until ${new Date(r.data.snoozed_until).toLocaleDateString()}`);
      fetchNotifications();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not snooze'); }
  };

  const [repricing, setRepricing] = useState(null);
  const repriceFromAlert = async (e, notif) => {
    e.stopPropagation();
    setRepricing(notif.id);
    try {
      const r = await axios.post(`${API_URL}/api/estimates/${notif.estimate_id}/reprice`, { notification_id: notif.id }, { withCredentials: true });
      if (r.data.changed) toast.success(r.data.message); else toast.info(r.data.message);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true, actioned: true } : n));
      if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not reprice'); } finally { setRepricing(null); }
  };

  const approveReprice = async (e, notif) => {
    e.stopPropagation();
    setRepricing(notif.id);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/reprice-approvals/${notif.estimate_id}/approve`, {}, { withCredentials: true });
      toast.success(r.data.message);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true, actioned: true } : n));
      if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not approve'); } finally { setRepricing(null); }
  };

  const approveCalibration = async (e, notif) => {
    e.stopPropagation();
    setRepricing(notif.id);
    try {
      const r = await axios.post(`${API_URL}/api/cac/calibration/approvals/${notif.estimate_id}/approve`, {}, { withCredentials: true });
      toast.success(`CAC factors applied — model v${r.data.version}`);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true, actioned: true } : n));
      if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not approve'); } finally { setRepricing(null); }
  };

  const handleClick = (notif) => {
    if (!notif.read) handleMarkRead(notif.id);
    setOpen(false);
    navigate(notif.link || `/workspace/${notif.estimate_id}`);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-md hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-4.5 h-4.5 ${unreadCount > 0 ? 'text-[#0F2040]' : 'text-slate-400'}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-rose-500 text-white text-[9px] font-bold px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {/* Subtle amber dot when there's no unread notification to avoid
            stealing attention — but there IS entity drift to nudge about. */}
        {unreadCount === 0 && entityDrift.count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
            data-testid="notif-entity-drift-dot"
            title={`${entityDrift.count} SOW${entityDrift.count === 1 ? '' : 's'} on a different entity`}
          />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#0F2040]">Notifications</span>
              {unreadCount > 0 && (
                <Badge className="bg-rose-100 text-rose-700 text-[8px] py-0 px-1.5">{unreadCount} new</Badge>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-[9px] text-[#4ABFCF] hover:underline flex items-center gap-0.5"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-2.5 h-2.5" /> Mark all read
              </button>
            )}
          </div>

          {/* Entity-drift pill — surfaces when one or more editable SOWs don't
              match the user's saved default entity. Clicking it deep-links to
              Profile → My SOWs so users can act without hunting. */}
          {entityDrift.count > 0 && (
            <button
              onClick={() => {
                setOpen(false);
                navigate('/profile?tab=sows');
              }}
              className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 hover:bg-amber-100 border-b border-amber-100 transition-colors text-left"
              data-testid="notif-entity-drift-pill"
              title="Open My SOWs to review and sync"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-3 h-3 text-amber-700" />
                </div>
                <div className="leading-tight">
                  <p className="text-[11px] font-semibold text-amber-900">
                    {entityDrift.count} SOW{entityDrift.count === 1 ? '' : 's'} on a different entity
                  </p>
                  <p className="text-[9px] text-amber-700/80">
                    Your draft SOWs don't match your default — review &amp; sync.
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-semibold text-amber-900 shrink-0">Review →</span>
            </button>
          )}

          {/* Notification list */}
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-[10px] text-slate-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const cfg = NOTIF_CONFIG[notif.type] || NOTIF_CONFIG.comment;
                const IconComp = cfg.icon;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-0 ${
                      notif.read ? 'opacity-60' : ''
                    }`}
                    data-testid={`notification-${notif.id}`}
                  >
                    {/* Icon */}
                    <div className={`w-7 h-7 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                      <IconComp className={`w-3.5 h-3.5 ${cfg.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] text-[#0F2040] leading-snug ${notif.read ? '' : 'font-medium'}`}>
                        {notif.message}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[9px] text-slate-400 truncate">{notif.estimate_name}</span>
                        <span className="text-[9px] text-slate-300">·</span>
                        <span className="text-[9px] text-slate-400 shrink-0">{timeAgo(notif.created_at)}</span>
                        {notif.type === 'rate_change' && !notif.actioned && (
                          <span role="button" tabIndex={0} onClick={(e) => repriceFromAlert(e, notif)} onKeyDown={(e) => { if (e.key === 'Enter') repriceFromAlert(e, notif); }}
                            className={`ml-auto text-[9px] font-semibold text-amber-800 border border-amber-300 rounded px-1 py-0 hover:bg-amber-50 shrink-0 ${repricing === notif.id ? 'opacity-60' : ''}`}
                            title="Recalculate this estimate at today's rate cards and save a new version" data-testid={`notification-reprice-${notif.id}`}>
                            {repricing === notif.id ? 'Repricing…' : 'Reprice now'}
                          </span>
                        )}
                        {notif.type === 'rate_change' && notif.actioned && (
                          <span className="ml-auto text-[9px] font-semibold text-emerald-700 shrink-0" data-testid={`notification-repriced-${notif.id}`}>Repriced ✓</span>
                        )}
                        {notif.type === 'reprice_approval' && !notif.actioned && /requests approval/.test(notif.message) && (
                          <span role="button" tabIndex={0} onClick={(e) => approveReprice(e, notif)} onKeyDown={(e) => { if (e.key === 'Enter') approveReprice(e, notif); }}
                            className={`ml-auto text-[9px] font-semibold text-[#0F2040] border border-[#4ABFCF] rounded px-1 py-0 hover:bg-cyan-50 shrink-0 ${repricing === notif.id ? 'opacity-60' : ''}`}
                            title="Confirm and run this bulk reprice" data-testid={`notification-approve-reprice-${notif.id}`}>
                            {repricing === notif.id ? 'Repricing…' : 'Approve & reprice'}
                          </span>
                        )}
                        {notif.type === 'reprice_approval' && notif.actioned && /requests approval/.test(notif.message) && (
                          <span className="ml-auto text-[9px] font-semibold text-emerald-700 shrink-0" data-testid={`notification-approved-${notif.id}`}>Approved ✓</span>
                        )}
                        {notif.type === 'cac_calibration_approval' && !notif.actioned && (
                          <span role="button" tabIndex={0} onClick={(e) => approveCalibration(e, notif)} onKeyDown={(e) => { if (e.key === 'Enter') approveCalibration(e, notif); }}
                            className={`ml-auto text-[9px] font-semibold text-[#0F2040] border border-[#4ABFCF] rounded px-1 py-0 hover:bg-cyan-50 shrink-0 ${repricing === notif.id ? 'opacity-60' : ''}`}
                            title="Approve and apply the suggested CAC factors" data-testid={`notification-approve-cac-${notif.id}`}>
                            {repricing === notif.id ? 'Applying…' : 'Approve & apply'}
                          </span>
                        )}
                        {notif.type === 'cac_calibration_approval' && notif.actioned && (
                          <span className="ml-auto text-[9px] font-semibold text-emerald-700 shrink-0" data-testid={`notification-cac-actioned-${notif.id}`}>Actioned ✓</span>
                        )}
                        {notif.type === 'factor_shift' && (
                          <span role="button" tabIndex={0} onClick={(e) => snoozeFactorShift(e)} onKeyDown={(e) => { if (e.key === 'Enter') snoozeFactorShift(e); }}
                            className="ml-auto text-[9px] font-semibold text-rose-700 border border-rose-200 rounded px-1 py-0 hover:bg-rose-50 shrink-0"
                            title="Pause factor shift alerts for you for 30 days" data-testid={`notification-snooze-${notif.id}`}>
                            Snooze 30d
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Unread dot */}
                    {!notif.read && (
                      <span className="w-2 h-2 rounded-full bg-[#4ABFCF] shrink-0 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
